import { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AppProvider } from './context';
import { useAuth } from './hooks/useAuth';
import { LoginScreen } from './modules/auth/screens/login';
import { DashboardLayout } from './components/layout/dashboard-layout';
import { ErrorBoundary } from './components/error-boundary';
import { Forbidden } from './components/forbidden';
import { FullPageLoader } from './components/full-page-loader';
import { NotFound } from './components/not-found';
import { PendingPage } from './components/pending-page';
import { CurrencyModeProvider } from './components/money-amount';
import { PermissionProvider, requirementForPath, usePermissions } from './permissions';
import { APP_ROUTES } from './routes';

/**
 * Gate in front of the dashboard shell. Every protected route renders through
 * it, so both the session check and the permission check live here rather than
 * being repeated per route.
 */
const RequireAuth = () => {
  const { isAuthenticated, isBootstrapping } = useAuth();
  const { can, status } = usePermissions();
  const location = useLocation();

  // A restored token is still being exchanged for a user — showing the login
  // screen here would flash it on every reload.
  if (isBootstrapping) {
    return <FullPageLoader />;
  }

  if (!isAuthenticated) {
    // Carry the attempted location so login can return the user to it, and
    // change the URL rather than rendering login under the protected path.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (status === 'loading') {
    return <FullPageLoader label="Yetkiler kontrol ediliyor..." />;
  }

  if (!can(requirementForPath(location.pathname))) {
    return <Forbidden />;
  }

  return <Outlet />;
};

/** Sends an already signed-in user away from the login screen. */
const LoginRoute = () => {
  const { isAuthenticated, isBootstrapping } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;

  if (isBootstrapping) return <FullPageLoader />;
  if (isAuthenticated) return <Navigate to={from ?? '/'} replace />;

  return <LoginScreen />;
};

const AppContent = () => (
  <Router>
    <Routes>
      <Route path="/login" element={<LoginRoute />} />

      <Route element={<RequireAuth />}>
        <Route element={<DashboardLayout />}>
          {APP_ROUTES.map(({ path, title, pending, element: Screen }) => (
            <Route
              key={path}
              path={path}
              element={pending ? <PendingPage title={title} /> : <Screen />}
            />
          ))}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  </Router>
);

export function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <PermissionProvider>
          <CurrencyModeProvider>
            <Suspense fallback={<FullPageLoader />}>
              <AppContent />
            </Suspense>
          </CurrencyModeProvider>
        </PermissionProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
